"use client";

import type { ConsentCategories } from "@guru/shared/consent";
import { cn } from "@guru/shared/utils/cn";
import { useEffect, useState } from "react";
import { Button } from "../button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../dialog";
import { Switch } from "../switch";
import { useConsent } from "./consent-context";

export interface CookiePreferencesCategory {
  id: "essential" | "analytics" | "sessionReplay";
  title: string;
  description: string;
}

export interface CookiePreferencesCopy {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  savePreferences: string;
  alwaysOn: string;
  categories: CookiePreferencesCategory[];
}

export interface CookiePreferencesProps {
  copy: CookiePreferencesCopy;
  className?: string;
}

/**
 * Granular preference centre. No pre-ticked boxes — non-essential toggles start
 * from the user's stored choice (default-denied). Copy is supplied by the app.
 */
export function CookiePreferences({ copy, className }: CookiePreferencesProps) {
  const { categories, gpc, acceptAll, rejectAll, save } = useConsent();
  const [draft, setDraft] = useState<ConsentCategories>(categories);

  // Re-sync the draft when stored consent hydrates or changes via the banner,
  // so toggles never display stale values nor overwrite an existing opt-in.
  useEffect(() => {
    setDraft(categories);
  }, [categories]);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <ul className="flex flex-col divide-y divide-border">
        {copy.categories.map((cat) => {
          const locked = cat.id === "essential" || gpc;
          const checked = cat.id === "essential" ? true : gpc ? false : draft[cat.id];
          return (
            <li key={cat.id} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">{cat.title}</p>
                <p className="text-sm text-muted-foreground">{cat.description}</p>
              </div>
              <Switch
                checked={checked}
                disabled={locked}
                aria-label={cat.title}
                onCheckedChange={(value) =>
                  setDraft((prev) => ({ ...prev, essential: true, [cat.id]: value }))
                }
              />
              {locked ? <span className="sr-only">{copy.alwaysOn}</span> : null}
            </li>
          );
        })}
      </ul>
      {gpc ? (
        <p className="text-sm text-muted-foreground">
          Your browser has Global Privacy Control enabled, so non-essential cookies stay off.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="default" size="sm" onClick={() => save(draft)}>
          {copy.savePreferences}
        </Button>
        <Button variant="default" size="sm" disabled={gpc} onClick={acceptAll}>
          {copy.acceptAll}
        </Button>
        <Button variant="outline" size="sm" onClick={rejectAll}>
          {copy.rejectAll}
        </Button>
      </div>
    </div>
  );
}

export function CookiePreferencesDialog({ copy }: { copy: CookiePreferencesCopy }) {
  const { preferencesOpen, setPreferencesOpen } = useConsent();
  return (
    <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <CookiePreferences copy={copy} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Persistent trigger for apps without a footer — lets users reopen the
 * preference centre after dismissing the banner. Hidden under GPC.
 */
export function CookiePreferencesTrigger({ label }: { label: string }) {
  const { hasDecided, gpc, openPreferences } = useConsent();
  if (!hasDecided || gpc) return null;
  return (
    <button
      type="button"
      onClick={openPreferences}
      className="fixed bottom-3 left-3 z-[999] rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm hover:text-foreground"
    >
      {label}
    </button>
  );
}
